/**
 * Plantillas HTML para exportar listado de productos a PDF (albarán y presupuesto).
 * CSS según skill html-to-pdf: container A4, sin background en body, overflow controlado.
 */

const currencyFormat = new Intl.NumberFormat('es-ES', {
  style: 'currency',
  currency: 'EUR',
});

type ListProduct = {
  id: number;
  name: string;
  units: number;
  price: number;
  reduced_price: number | false;
  total: number;
  reduced_total: number | false;
  image?: string;
  url?: string;
  discount?: number | false;
};

/** Tramo del resumen: datos para las instrucciones de instalación por tramo */
export type TramoData = {
  tramo?: number;
  long?: number;
  paneles?: number;
  cortado?: number;
  division?: number | false;
  aprovechados?: { tramo_origen: number }[];
  start?: number | null;
  end?: number;
  middles?: number;
  cortes?: number;
};

export type ResumeData = {
  listProducts: ListProduct[];
  total: number;
  tramos?: TramoData[];
  products?: Record<string, ListProduct>;
  panel?: number;
  middle?: number;
  corte?: number;
};

const PDF_STYLES = `
  <style>
    .pdf-export-root { width: 210mm; max-width: 100%; box-sizing: border-box; }
    .pdf-container {
      width: 100%;
      max-width: 100%;
      padding: 15mm;
      padding-bottom: 24mm;
      box-sizing: border-box;
      position: relative;
      min-height: 267mm;
      overflow-wrap: break-word;
      word-wrap: break-word;
      font-family: Arial, sans-serif;
      font-size: 11px;
      color: #1f140f;
    }
    .pdf-container h1 { font-size: 18px; margin: 0 0 12px 0; color: #1f140f; }
    .pdf-container h2 { font-size: 14px; margin: 16px 0 8px 0; color: #1f140f; }
    .pdf-container table { width: 100%; border-collapse: collapse; margin-top: 8px; }
    .pdf-container th, .pdf-container td { border: 1px solid #ddd; padding: 6px 8px; text-align: left; }
    .pdf-container th { background: #E6E6E6; font-weight: bold; }
    .pdf-container .text-right { text-align: right; }
    .pdf-container .text-metal { color: rgb(214, 31, 19); }
    .pdf-container .meta { margin-bottom: 12px; color: #666; font-size: 10px; }
    .pdf-container .pdf-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 10mm; margin-bottom: 6mm; }
    .pdf-container .pdf-logo { height: 14mm; width: auto; object-fit: contain; }
    .pdf-container .pdf-title-wrap { display: flex; flex-direction: column; align-items: flex-start; }
    .pdf-container .pdf-title-wrap h1 { margin: 0 0 3mm 0; }
    .pdf-container .pdf-title-wrap .meta { margin: 0; }
    .pdf-container .pdf-footer {
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      font-size: 10px;
      color: #666;
      text-transform: uppercase;
      letter-spacing: 0.08em;
    }
    .pdf-container .product-cell { display: flex; align-items: center; gap: 6px; }
    .pdf-container .product-thumb { width: 32px; height: 32px; object-fit: contain; }
    .pdf-container .pdf-instrucciones { margin-top: 16px; }
    .pdf-container .pdf-instrucciones h2 { margin-bottom: 10px; }
    .pdf-container .tramo-card { margin-bottom: 10px; padding: 8px 10px; border: 1px solid #ddd; border-radius: 4px; break-inside: avoid; }
    .pdf-container .tramo-card h3 { font-size: 12px; margin: 0 0 6px 0; color: #1f140f; }
    .pdf-container .tramo-card p { margin: 0 0 4px 0; line-height: 1.35; }
    .pdf-container .tramo-card p:last-child { margin-bottom: 0; }
  </style>
`;

const PDF_LOGO_URL = 'https://www.decofence.es/img/logo-1693984651.jpg';
const PDF_FOOTER_TEXT = 'METÁLICAS CRIADO, S.L';
const PDF_FOOTER_NOTE = '*Cotas aproximadas a ajustar durante la instalación.';

function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

function getProductName(
  products: Record<string, ListProduct> | undefined,
  id: number
): string {
  const p = products?.[`p-${id}`];
  return p?.name ?? `Ref. ${id}`;
}

/**
 * Genera HTML de la sección "Instrucciones para la instalación" a partir de resume.tramos.
 */
export function buildInstruccionesHtml(resume: ResumeData): string {
  const tramos = resume.tramos ?? [];
  const products = resume.products;
  const panel = resume.panel;
  const middle = resume.middle;
  const corte = resume.corte;

  if (tramos.length === 0) return '';

  const cards = tramos
    .map((tramo) => {
      const tramoIndex = (tramo.tramo ?? 0) + 1;
      const long = tramo.long ?? 0;
      const panelName =
        panel !== undefined ? getProductName(products, panel) : '';
      const startName =
        tramo.start != null ? getProductName(products, tramo.start) : '';
      const endName =
        tramo.end != null ? getProductName(products, tramo.end) : '';
      const middleName =
        middle !== undefined ? getProductName(products, middle) : '';
      const corteName =
        corte !== undefined ? getProductName(products, corte) : '';
      const aprovechado =
        tramo.aprovechados?.length === 1
          ? tramo.aprovechados[0]
          : null;
      const tramoOrigen =
        aprovechado ? (aprovechado.tramo_origen + 1) : 0;
      const cortado = tramo.cortado;
      const division = tramo.division;
      const paneles = tramo.paneles ?? 0;
      const middles = tramo.middles ?? 0;
      const cortes = tramo.cortes ?? 0;

      const lines: string[] = [];

      if (paneles === 1) {
        let line = `Un panel ${escapeHtml(panelName)}`;
        if (cortado !== undefined) line += ` cortado a ${cortado}cm`;
        if (
          division === false &&
          aprovechado &&
          cortado !== undefined
        ) {
          line += `, puede aprovechar el sobrante del tramo #${tramoOrigen} para cortar los ${cortado}cm necesarios.`;
        }
        line += '.';
        lines.push(`<p>${line}</p>`);
      } else if (paneles > 1) {
        let line = `Paneles ${escapeHtml(panelName)} necesarios: ${paneles}`;
        if (
          division === false &&
          aprovechado &&
          cortado !== undefined
        ) {
          line += `, puede aprovechar el sobrante del tramo #${tramoOrigen} para cortar los ${cortado}cm necesarios.`;
        }
        line += '.';
        lines.push(`<p>${line}</p>`);
      } else if (aprovechado && cortado !== undefined) {
        lines.push(
          `<p>No requiere paneles ${escapeHtml(panelName)} adicionales porque puede aprovechar el sobrante del tramo <strong>#${tramoOrigen}</strong> para cortar los ${cortado}cm necesarios.</p>`
        );
      }

      if (division !== false && division !== undefined) {
        lines.push(
          `<p>No es posible instalar un panel inferior a <i>20cm</i> porque el corte de <i>${division}cm</i> no se puede montar. Recomendamos cortar <b>2 paneles a ${cortado ?? ''}cm</b>.</p>`
        );
      }
      if (tramo.start != null) {
        lines.push(`<p>Iniciar con ${escapeHtml(startName)}.</p>`);
      }
      if (middles > 0) {
        let line = `Se requiere ${middles} de ${escapeHtml(middleName)}`;
        if (cortes > 0) {
          line += ` y ${cortes} ${escapeHtml(corteName)}`;
        }
        line += '.';
        lines.push(`<p>${line}</p>`);
      } else if (cortes > 0) {
        lines.push(
          `<p>Se requiere ${cortes} de ${escapeHtml(corteName)}.</p>`
        );
      }
      lines.push(`<p>Para terminar ${escapeHtml(endName)}.</p>`);

      return `
        <div class="tramo-card no-break">
          <h3>Tramo <strong>#${tramoIndex}</strong>, ${long}cm</h3>
          ${lines.join('')}
        </div>`;
    })
    .join('');

  return `
    <div class="pdf-instrucciones">
      <h2>Instrucciones para la instalación</h2>
      ${cards}
    </div>`;
}

/**
 * Genera HTML para albarán / listado de entrega (sin precios).
 */
export function buildAlbaranHtml(resume: ResumeData): string {
  const date = new Date().toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const rows = resume.listProducts
    .map(
      (p) => `
    <tr>
      <td>${escapeHtml(String(p.id))}</td>
      <td>${escapeHtml(p.name)}</td>
      <td class="text-right">${p.units}</td>
      <td></td>
    </tr>`
    )
    .join('');

  return `
  <div class="pdf-export-root">${PDF_STYLES}
    <div class="pdf-container">
      <div class="pdf-header">
        <img src="${PDF_LOGO_URL}" alt="DECOFENCE" class="pdf-logo" crossorigin="anonymous" />
        <div class="pdf-title-wrap">
          <h1>Albarán / Listado de entrega</h1>
          <div class="meta">Fecha: ${date}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Producto</th>
            <th class="text-right">Cantidad</th>
            <th>Observaciones</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      <div class="pdf-footer">
        <div>${escapeHtml(PDF_FOOTER_NOTE)}</div>
        <div>${PDF_FOOTER_TEXT}</div>
      </div>
    </div>
  </div>`;
}

/**
 * Genera HTML para presupuesto (con precios y total).
 */
export function buildPresupuestoHtml(resume: ResumeData): string {
  const date = new Date().toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

  const rows = resume.listProducts
    .map(
      (p) => {
        const unitPrice = p.reduced_price !== false ? p.reduced_price : p.price;
        const lineTotal = p.reduced_total !== false ? p.reduced_total : p.total;
        const imageHtml = p.image
          ? `<img src="${escapeHtml(p.image)}" alt="${escapeHtml(
              p.name
            )}" class="product-thumb" />`
          : '';
        return `
    <tr>
      <td>${escapeHtml(String(p.id))}</td>
      <td>
        <div class="product-cell">
          ${imageHtml}
          <span>${escapeHtml(p.name)}</span>
        </div>
      </td>
      <td class="text-right">${p.units}</td>
      <td class="text-right">${currencyFormat.format(unitPrice)}</td>
      <td class="text-right">${currencyFormat.format(lineTotal)}</td>
    </tr>`;
      }
    )
    .join('');

  const totalFormatted = currencyFormat.format(resume.total);

  return `
  <div class="pdf-export-root">${PDF_STYLES}
    <div class="pdf-container">
      <div class="pdf-header">
        <img src="${PDF_LOGO_URL}" alt="DECOFENCE" class="pdf-logo" crossorigin="anonymous" />
        <div class="pdf-title-wrap">
          <h1>Presupuesto</h1>
          <div class="meta">Fecha: ${date}</div>
        </div>
      </div>
      <table>
        <thead>
          <tr>
            <th>Ref.</th>
            <th>Producto</th>
            <th class="text-right">Cantidad</th>
            <th class="text-right">P. unitario</th>
            <th class="text-right">Total</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td colspan="4" class="text-right"><strong>Total</strong></td>
            <td class="text-right text-metal"><strong>${totalFormatted}</strong></td>
          </tr>
        </tfoot>
      </table>
      ${buildInstruccionesHtml(resume)}
      <div class="pdf-footer">
        <div>${escapeHtml(PDF_FOOTER_NOTE)}</div>
        <div>${PDF_FOOTER_TEXT}</div>
      </div>
    </div>
  </div>`;
}

/**
 * Devuelve el HTML completo según el tipo de documento.
 */
export function buildPdfHtml(
  type: 'albaran' | 'presupuesto',
  resume: ResumeData
): string {
  return type === 'albaran'
    ? buildAlbaranHtml(resume)
    : buildPresupuestoHtml(resume);
}
